import { supabase } from '../lib/supabase';
import { openaiService } from './openaiService';

// Support Service for managing tickets, chat, and FAQs
class SupportService {
  constructor() {
    this.listeners = new Set();
    this.chatListeners = new Set();
    this.activeSession = null;
  }

  // ============================================================================
  // FAQ OPERATIONS
  // ============================================================================

  async getFAQs(category = null) {
    try {
      let query = supabase?.from('support_faqs')?.select('*')?.eq('is_active', true)?.order('order_index', { ascending: true });

      if (category) {
        query = query?.eq('category', category);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      return { data: null, error: error?.message };
    }
  }

  async incrementFAQView(faqId) {
    try {
      const { error } = await supabase?.rpc('increment', {
        table_name: 'support_faqs',
        row_id: faqId,
        column_name: 'view_count'
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error incrementing FAQ view:', error);
    }
  }

  async markFAQHelpful(faqId) {
    try {
      const { data, error } = await supabase?.from('support_faqs')?.select('helpful_count')?.eq('id', faqId)?.single();

      if (error) throw error;

      const { error: updateError } = await supabase?.from('support_faqs')?.update({ helpful_count: (data?.helpful_count || 0) + 1 })?.eq('id', faqId);

      if (updateError) throw updateError;
      return { success: true, error: null };
    } catch (error) {
      console.error('Error marking FAQ helpful:', error);
      return { success: false, error: error?.message };
    }
  }

  // ============================================================================
  // TICKET OPERATIONS
  // ============================================================================

  async createTicket(ticketData) {
    try {
      const { data, error } = await supabase?.from('support_tickets')?.insert([{
          user_id: ticketData?.userId,
          organization_id: ticketData?.organizationId,
          subject: ticketData?.subject,
          description: ticketData?.description,
          category: ticketData?.category,
          priority: ticketData?.priority || 'medium'
        }])?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating ticket:', error);
      return { data: null, error: error?.message };
    }
  }

  async getMyTickets(userId) {
    try {
      const { data, error } = await supabase?.from('support_tickets')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching tickets:', error);
      return { data: null, error: error?.message };
    }
  }

  async updateTicketStatus(ticketId, status) {
    try {
      const { data, error } = await supabase?.from('support_tickets')?.update({ status })?.eq('id', ticketId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating ticket status:', error);
      return { data: null, error: error?.message };
    }
  }

  // ============================================================================
  // CHAT OPERATIONS
  // ============================================================================

  async startChatSession(userId, organizationId) {
    try {
      const { data, error } = await supabase?.from('support_chat_sessions')?.insert([{
          user_id: userId,
          organization_id: organizationId,
          status: 'waiting'
        }])?.select()?.single();

      if (error) throw error;

      // Send initial system message
      await this.sendChatMessage(data?.id, userId, 'A support representative will be with you shortly. Average wait time is 2 minutes.', true);

      this.activeSession = data;
      return { data, error: null };
    } catch (error) {
      console.error('Error starting chat session:', error);
      return { data: null, error: error?.message };
    }
  }

  async sendChatMessage(sessionId, senderId, message, isSystemMessage = false) {
    try {
      const { data, error } = await supabase?.from('support_chat_messages')?.insert([{
          session_id: sessionId,
          sender_id: senderId,
          message,
          is_system_message: isSystemMessage
        }])?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error sending chat message:', error);
      return { data: null, error: error?.message };
    }
  }

  async getChatMessages(sessionId) {
    try {
      const { data, error } = await supabase?.from('support_chat_messages')?.select(`
          *,
          sender:sender_id(full_name, avatar_url)
        `)?.eq('session_id', sessionId)?.order('created_at', { ascending: true });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching chat messages:', error);
      return { data: null, error: error?.message };
    }
  }

  async endChatSession(sessionId) {
    try {
      const { data, error } = await supabase?.from('support_chat_sessions')?.update({ 
          status: 'closed',
          ended_at: new Date()?.toISOString()
        })?.eq('id', sessionId)?.select()?.single();

      if (error) throw error;
      this.activeSession = null;
      return { data, error: null };
    } catch (error) {
      console.error('Error ending chat session:', error);
      return { data: null, error: error?.message };
    }
  }

  // ============================================================================
  // AI-POWERED CHAT SUPPORT
  // ============================================================================

  async generateAIResponse(userMessage, chatHistory = []) {
    try {
      const systemPrompt = `You are a helpful support assistant for MyDojo, a Brazilian Jiu-Jitsu academy management platform. 

Your role:
- Provide clear, friendly, and professional support
- Help users with platform features, billing questions, technical issues, and general inquiries
- Be concise but thorough (2-4 sentences)
- Use a warm, supportive tone
- If you don't know something, acknowledge it and suggest contacting human support - Respond in the same language as the user's message (Portuguese or English)

Key MyDojo Features:
- Student Management: Track students, attendance, belt progression
- Attendance Tracking: QR code check-in, manual attendance, punctuality monitoring
- Financial Dashboard: Revenue tracking, billing management, Stripe integration
- Schedule Management: Class scheduling, instructor assignment
- Communication Center: SMS/email notifications, emergency alerts
- Belt Progression: Automated eligibility tracking, promotion pipeline
- Competition Management: Tournament registration, performance analytics
- AI Training Plans: Personalized development plans for students

Billing:
- Free 60-day trial for new academies
- Plans: Basic (R$59), Pro (R$129), Unlimited (R$179)
- Payment methods: Credit card (Stripe), PIX, Boleto (Brazil)
- Automatic plan upgrades based on student count`;

      const messages = [
        { role: 'system', content: systemPrompt },
        ...chatHistory?.map(msg => ({
          role: msg?.is_system_message ? 'system' : (msg?.sender_id === 'ai' ? 'assistant' : 'user'),
          content: msg?.message
        })),
        { role: 'user', content: userMessage }
      ];

      const response = await openaiService?.generateChatResponse(messages);
      
      if (response?.error) {
        return {
          data: null,
          error: response?.error
        };
      }

      return {
        data: response?.data,
        error: null
      };
    } catch (error) {
      console.error('Error generating AI response:', error);
      return {
        data: null,
        error: { message: 'Failed to generate AI response. Please try again.' }
      };
    }
  }

  // ============================================================================
  // REAL-TIME SUBSCRIPTIONS
  // ============================================================================

  subscribeToChatMessages(sessionId, callback) {
    const subscription = supabase?.channel(`chat_messages:${sessionId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'support_chat_messages',
          filter: `session_id=eq.${sessionId}`
        },
        (payload) => {
          callback(payload?.new);
        }
      )?.subscribe();

    this.chatListeners?.add(subscription);
    return () => {
      subscription?.unsubscribe();
      this.chatListeners?.delete(subscription);
    };
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  getActiveSession() {
    return this.activeSession;
  }

  clearActiveSession() {
    this.activeSession = null;
  }
}

// Export singleton instance
export const supportService = new SupportService();
export default supportService;